/**
 * SectionNaButton — Admin/PM control on each category header to mark
 * (or restore) the entire section as Not Applicable for this project.
 *
 * When marked N/A the section header goes gray + shows a badge, the
 * item grid collapses, uploads to that category get 409'd at the API,
 * and the whole section is excluded from the customer closeout PDF.
 * A restore click returns everything to normal — any photos captured
 * before the N/A were preserved, they were only hidden from the PDF.
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Ban, RotateCcw } from "lucide-react";

export default function SectionNaButton({ projectId, categoryName, isNa, reason, onChanged }) {
  const [open, setOpen] = useState(false);
  const [draftReason, setDraftReason] = useState(reason || "");
  const [busy, setBusy] = useState(false);

  const testKey = categoryName.replace(/\s+/g, "-").toLowerCase();

  const mark = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/na-category`, {
        category: categoryName,
        reason: draftReason.trim(),
      });
      toast.success(`Section "${categoryName}" marked N/A`);
      setOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark section N/A");
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    setBusy(true);
    try {
      await api.delete(`/projects/${projectId}/na-category/${encodeURIComponent(categoryName)}`);
      toast.success(`Section "${categoryName}" restored`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not restore section");
    } finally {
      setBusy(false);
    }
  };

  if (isNa) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={restore}
        disabled={busy}
        className="h-7 px-2 text-[11px] border-slate-700 hover:bg-slate-800"
        data-testid={`section-na-restore-${testKey}`}
      >
        <RotateCcw className="w-3 h-3 mr-1" />
        Restore
      </Button>
    );
  }

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen(true)}
        className="h-7 px-2 text-[11px] text-slate-400 hover:text-amber-300 hover:bg-amber-950/40"
        data-testid={`section-na-mark-${testKey}`}
      >
        <Ban className="w-3 h-3 mr-1" />
        Mark N/A
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800" data-testid={`section-na-dialog-${testKey}`}>
          <DialogHeader>
            <DialogTitle>Mark section N/A: {categoryName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-300">
            <p>
              This section will be <b>excluded from the customer closeout PDF</b> and
              new uploads to it will be blocked. Any photos already captured stay
              attached — they're just hidden from the deliverable.
            </p>
            <p className="text-xs text-slate-500">
              Restore anytime from the same section header.
            </p>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-mono block mb-1.5">
                Reason (optional but recommended for audit)
              </label>
              <Input
                value={draftReason}
                onChange={(e) => setDraftReason(e.target.value)}
                placeholder='e.g. "No ATC work on this scope"'
                className="bg-slate-950 border-slate-800"
                data-testid={`section-na-reason-input-${testKey}`}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button
              onClick={mark}
              disabled={busy}
              className="bg-amber-700 hover:bg-amber-600 text-white"
              data-testid={`section-na-confirm-${testKey}`}
            >
              {busy ? "Marking…" : "Mark N/A"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
